import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { article } from '@/mocks/article';
import { mainSiteUrl, categories, blogArticles } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cssbuy.wiki';

export default function ArticlePage() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  const currentArticle = blogArticles.find((a) => a.slug === slug) || blogArticles[0];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: currentArticle.title,
    description: currentArticle.description,
    author: {
      '@type': 'Organization',
      name: 'CSSBuy.wiki',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'CSSBuy.wiki',
      url: siteUrl,
    },
    datePublished: '2026-01-15',
    dateModified: '2026-06-27',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/artigo/${currentArticle.slug}`,
    },
    inLanguage: 'pt-BR',
    about: {
      '@type': 'Thing',
      name: 'CSSBuy Spreadsheet',
      description: 'Planilha inteligente para organizar compras internacionais da China com segurança e economia.',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <main className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      {/* Navigation */}
      <nav className="sticky top-0 z-50 h-[72px] flex items-center px-4 md:px-10 bg-background-50/95 backdrop-blur-xl border-b border-background-200/70">
        <div className="w-full max-w-[900px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1.5 text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer text-sm font-medium"
            >
              <i className="ri-arrow-left-line"></i>
              <span className="hidden sm:inline">Voltar ao início</span>
            </button>
          </div>
          <button
            onClick={() => navigate('/')}
            className="text-foreground-900 font-heading text-lg font-bold tracking-tight whitespace-nowrap hover:opacity-80 transition-opacity cursor-pointer"
          >
            CSSBuy<span className="font-light italic text-foreground-600">.wiki</span>
          </button>
          <div className="w-16"></div>
        </div>
      </nav>

      {/* Article Header */}
      <header className="bg-background-100 border-b border-background-200/50">
        <div className="max-w-[900px] mx-auto px-4 md:px-10 py-14 md:py-20">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">Guia Completo</span>
            <span className="text-foreground-400 text-xs">2026</span>
          </div>
          <h1 className="font-heading text-foreground-900 text-3xl md:text-4xl lg:text-5xl font-black leading-tight mb-4">
            {currentArticle.title}
          </h1>
          <p className="text-foreground-500 text-base md:text-lg leading-relaxed max-w-2xl">
            {currentArticle.description}
          </p>
        </div>
      </header>

      {/* Article Content */}
      <article className="max-w-[900px] mx-auto px-4 md:px-10 py-10 md:py-16">
        <div className="space-y-14">
          {article.sections.map((section, sIndex) => (
            <section key={sIndex}>
              <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl font-bold mb-6">
                {section.heading}
              </h2>
              <div className="space-y-5">
                {section.content.map((paragraph, pIndex) => (
                  <p key={pIndex} className="text-foreground-600 text-base leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Internal Links */}
        <div className="mt-16 p-6 md:p-8 bg-background-100 rounded-2xl">
          <h3 className="font-heading text-foreground-900 text-xl font-bold mb-4">
            Explore mais sobre CSSBuy Spreadsheet
          </h3>
          <div className="flex flex-wrap gap-3">
            {categories.slice(0, 8).map((cat) => (
              <a
                key={cat.seq}
                href={cat.link}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-background-50 text-foreground-600 text-sm hover:bg-background-200 transition-colors cursor-pointer"
              >
                <span>{cat.namePt}</span>
                <i className="ri-arrow-right-up-line text-xs"></i>
              </a>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            {blogArticles.slice(4, 9).map((a, i) => (
              <button
                key={i}
                onClick={() => { navigate(`/artigo/${a.slug}`); window.scrollTo({ top: 0, behavior: 'auto' }); }}
                className="text-sm text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer underline underline-offset-2"
              >
                {a.title}
              </button>
            ))}
          </div>
          <div className="mt-4">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer inline-flex items-center gap-1"
            >
              <i className="ri-home-4-line"></i>
              <span>← Voltar para a página inicial do CSSBuy.wiki</span>
            </button>
          </div>
        </div>

        {/* FAQ Section */}
        <section className="mt-16">
          <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl font-bold mb-8">
            Perguntas Frequentes
          </h2>
          <div className="space-y-3">
            {article.faq.map((item, fIndex) => (
              <details key={fIndex} className="group bg-background-100 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between p-5 md:p-6 cursor-pointer hover:bg-background-200/70 transition-colors list-none">
                  <h3 className="text-foreground-900 font-semibold text-base pr-8">{item.q}</h3>
                  <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                  <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                </summary>
                <div className="px-5 md:px-6 pb-5 md:pb-6">
                  <p className="text-foreground-600 text-sm leading-relaxed">{item.a}</p>
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="mt-16 text-center bg-foreground-900 rounded-2xl p-10 md:p-14">
          <h2 className="font-heading text-background-50 text-2xl md:text-3xl font-bold mb-4">
            {article.ctaText}
          </h2>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-background-50 text-foreground-900 text-sm md:text-base font-semibold hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer group mt-4"
          >
            <span>Buy It Now</span>
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
          </a>
        </div>

        {/* Back to home */}
        <div className="mt-10 text-center">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer text-sm"
          >
            <i className="ri-arrow-left-line"></i>
            <span>← Voltar para CSSBuy.wiki</span>
          </button>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-foreground-900 text-background-50">
        <div className="max-w-[900px] mx-auto px-4 md:px-10 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <button onClick={() => navigate('/')} className="text-lg font-heading font-bold tracking-tight hover:opacity-80 transition-opacity cursor-pointer">
              CSSBuy<span className="font-light italic text-foreground-400">.wiki</span>
            </button>
            <div className="flex items-center gap-5">
              <button onClick={() => navigate('/')} className="text-foreground-500 text-xs hover:text-background-50 transition-colors cursor-pointer">Termos</button>
              <button onClick={() => navigate('/')} className="text-foreground-500 text-xs hover:text-background-50 transition-colors cursor-pointer">Privacidade</button>
              <button onClick={() => navigate('/')} className="text-foreground-500 text-xs hover:text-background-50 transition-colors cursor-pointer">Sobre</button>
            </div>
          </div>
          <div className="mt-4 text-center">
            <p className="text-foreground-500 text-xs">
              &copy; {new Date().getFullYear()} CSSBuy.wiki. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
}